import React, { useEffect, useState } from "react";
import { Card, Descriptions, Avatar, Skeleton } from "antd";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import { getDatabase, ref } from "firebase/database";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuth } from "firebase/auth";
import { Toolbar } from "@mui/material";
import { Button } from "evergreen-ui";
import { UserOutlined } from "@ant-design/icons";
import { getUserStatusLabel } from "../../../constants/userStatus";
import { getCallable } from "../../Firebase/functionsClient";

const db = getDatabase(Firebase);
const auth = getAuth(Firebase);
const listVolunteerTypesFn = getCallable("listVolunteerTypes");

const ViewUser = ({ selectedUser, onClose }) => {
	const [loggedUser, authLoading] = useAuthState(auth);

	const [arrayUsers, usersLoading] = useListVals(ref(db, "talmidsdb/users"));
	const [countryArray, countriesLoading] = useListVals(ref(db, "talmidsdb/countries"));
	const [communityArray, communitiesLoading] = useListVals(ref(db, "talmidsdb/communities"));
	const [orgArray, orgsLoading] = useListVals(ref(db, "talmidsdb/organisations"));
	const [roleArray, rolesLoading] = useListVals(ref(db, "talmidsdb/userRoles"));
	const [volunteerArray, setVolunteerArray] = useState([]);
	const [volunteerLoading, setVolunteerLoading] = useState(false);
	const [lessonTypes, lessonsLoading] = useListVals(ref(db, "talmidsdb/lessonTypes"));

	const [user, setUser] = useState(
		selectedUser?.user || selectedUser || null
	);

	useEffect(() => {
		const baseUser = selectedUser?.user || selectedUser || null;
		if (baseUser && arrayUsers && arrayUsers.length > 0) {
			const fullUser = arrayUsers.find((u) => u.id === baseUser.id) || {};
			setUser({ ...baseUser, ...fullUser });
		} else {
			setUser(baseUser);
		}
	}, [selectedUser, arrayUsers]);

	useEffect(() => {
		let isMounted = true;

		const loadVolunteerTypes = async () => {
			if (authLoading) return;
			if (!loggedUser) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerLoading(false);
				}
				return;
			}

			setVolunteerLoading(true);
			try {
				const res = await listVolunteerTypesFn({});
				const types = Array.isArray(res?.data?.volunteerTypes) ? res.data.volunteerTypes : [];
				if (isMounted) {
					setVolunteerArray(types);
				}
			} catch (_error) {
				if (isMounted) {
					setVolunteerArray([]);
				}
			} finally {
				if (isMounted) {
					setVolunteerLoading(false);
				}
			}
		};

		loadVolunteerTypes();

		return () => {
			isMounted = false;
		};
	}, [authLoading, loggedUser]);

	const loggedUserRecord = arrayUsers?.find((x) => x.id === loggedUser?.uid);
	const isSuperUser = loggedUserRecord?.userRole === "SuperUser";
	const canViewSensitive = isSuperUser || user?.id === loggedUser?.uid;

	const getLabel = (array, idField, valueField, matchValue) =>
		array?.find((x) => x[idField] === matchValue || x.id === matchValue || x.orgID === matchValue || x.communityID === matchValue)?.[valueField] || "Unknown";

	const getLabelsFromArray = (array, idField, valueField, ids) => {
		if (!array || !ids) return [];
		const normalizedIds = Array.isArray(ids) ? ids : [ids];

		return normalizedIds
			.map((id) => array.find((x) => x[idField] === id || x.id === id || x.orgID === id || x.communityID === id)?.[valueField])
			.filter(Boolean);
	};

	const getSupervisorName = () => {
		if (!user?.supervisor || !arrayUsers) return "Unknown";
		const supervisor = arrayUsers.find((x) => x.id === user.supervisor || x.userID === user.supervisor);
		if (!supervisor) return "Unknown";
		const first = supervisor.firstname || "";
		const last = supervisor.surname || "";
		const full = `${first} ${last}`.trim();
		return full || "Unknown";
	};

	const getVolunteerTypes = () => {
		if (volunteerLoading) return "Loading...";
		if (!user?.volunteerType || !volunteerArray) return "Unknown";

		const ids = Array.isArray(user.volunteerType)
			? user.volunteerType
			: [user.volunteerType];

		const labels = ids
			.map((id) =>
				volunteerArray.find((v) => v.id === id || v.volunteerTypeID === id || v.volunteerTypeCode === id)?.volunteerType
			)
			.filter(Boolean);

		return labels.length ? labels.join(", ") : "Unknown";
	};

	const getLessonsAttendedDisplay = () => {
		if (!user?.lessonsAttended || !lessonTypes) return "None";

		const attendedEntries = Object.entries(user.lessonsAttended).filter(
			([, attended]) => !!attended
		);
		if (!attendedEntries.length) return "None";

		const names = attendedEntries
			.map(([lessonTypeId]) => {
				const lt = lessonTypes.find((l) => l.id === lessonTypeId || l.lessonTypeID === lessonTypeId);
				return (
					lt?.typeName
				);
			})
			.filter(Boolean);

		return names.length ? names.join(", ") : "None";
	};

	const countryIds =
		(user?.countries && Array.isArray(user.countries) && user.countries.length
			? user.countries
			: user?.countryID
				? [user.countryID]
				: []) || [];

	const countryNames = getLabelsFromArray(
		countryArray,
		"id",
		"countryName",
		countryIds
	);
	const countryDisplay =
		countryNames.length > 0 ? countryNames.join(", ") : "Unknown";

	const orgIds =
		(user?.organisations &&
			Array.isArray(user.organisations) &&
			user.organisations.length
			? user.organisations
			: user?.organisationID
				? [user.organisationID]
				: []) || [];

	const orgNames = getLabelsFromArray(orgArray, "id", "name", orgIds);
	const orgDisplay =
		orgNames.length > 0 ? orgNames.join(", ") : "Unknown";

	const communityIds =
		(user?.communities &&
			Array.isArray(user.communities) &&
			user.communities.length
			? user.communities
			: user?.communityID
				? [user.communityID]
				: []) || [];

	const communityNames = getLabelsFromArray(
		communityArray,
		"id",
		"name",
		communityIds
	);
	const communityDisplay =
		communityNames.length > 0 ? communityNames.join(", ") : "Unknown";

	const fullName = [user?.firstname, user?.surname].filter(Boolean).join(" ");

	const avatarInitial =
		(user?.firstname && user.firstname[0]) ||
		(user?.surname && user.surname[0]) ||
		user?.email?.[0] ||
		"";

	const cardTitle = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				padding: 16,
			}}
		>
			<div style={{ fontSize: 16, fontWeight: 600 }}>
				{fullName || user?.name || "User details"}
			</div>
			<Avatar
				size={48}
				icon={!avatarInitial && <UserOutlined />}
				style={{ backgroundColor: "#1890ff" }}
			>
				{avatarInitial}
			</Avatar>
		</div>
	);

	const isLoading = authLoading || usersLoading || countriesLoading || communitiesLoading || orgsLoading || rolesLoading || lessonsLoading || volunteerLoading;

	if (isLoading) {
		return (
			<>
				<Toolbar />
				<Card bordered style={{ margin: 10 }}>
					<Skeleton active avatar paragraph={{ rows: 10 }} />
				</Card>
			</>
		);
	}

	if (!user) return null;

	return (
		<>
			<Toolbar />
			<Card title={cardTitle} bordered style={{ margin: 10 }}>
				<Descriptions column={1} bordered size="small">
					<Descriptions.Item label="Full Name">
						{fullName || "Unknown"}
					</Descriptions.Item>
					<Descriptions.Item label="Phone">
						{canViewSensitive ? user?.phone || "Unknown" : "Hidden"}
					</Descriptions.Item>
					<Descriptions.Item label="Email">
						{canViewSensitive ? user?.email || "Unknown" : "Hidden"}
					</Descriptions.Item>
					<Descriptions.Item label="Supervisor">
						{getSupervisorName()}
					</Descriptions.Item>

					<Descriptions.Item label="Countries">
						{countryDisplay}
					</Descriptions.Item>

					<Descriptions.Item label="Communities / Regions">
						{canViewSensitive ? communityDisplay : "Hidden"}
					</Descriptions.Item>

					<Descriptions.Item label="Organisations">
						{orgDisplay}
					</Descriptions.Item>

					<Descriptions.Item label="Volunteer Type">
						{getVolunteerTypes()}
					</Descriptions.Item>
					<Descriptions.Item label="User Role">
						{getLabel(
							roleArray,
							"userRoleID",
							"roleName",
							user?.userRole
						)}
					</Descriptions.Item>
					<Descriptions.Item label="Status">
						{getUserStatusLabel(user?.userStatus) || "Unknown"}
					</Descriptions.Item>

					<Descriptions.Item label="Lessons Attended">
						{getLessonsAttendedDisplay()}
					</Descriptions.Item>

					{user?.gender && (
						<Descriptions.Item label="Gender">
							{user.gender}
						</Descriptions.Item>
					)}
					{user?.dateOfBirth && (
						<Descriptions.Item label="Date of Birth">
							{user.dateOfBirth}
						</Descriptions.Item>
					)}
					{user?.dateAdded && (
						<Descriptions.Item label="Date Added">
							{user.dateAdded}
						</Descriptions.Item>
					)}
					{user?.lastLogin && (
						<Descriptions.Item label="Last Login">
							{user.lastLogin}
						</Descriptions.Item>
					)}
				</Descriptions>

				<div
					style={{
						marginTop: 16,
						display: "flex",
						justifyContent: "flex-end",
					}}
				>
					<Button appearance="warning" onClick={onClose}>
						Close
					</Button>
				</div>
			</Card>
		</>
	);
};

export default ViewUser;
